import { NetworkStatus, useQuery } from '@apollo/client';
import { Box, Button, ButtonSize, Icon, IconSize, ModalBody } from '@bechtel/gds';
import { BlockPoint, BreakerPanel, BreakerPanelInput, BreakerPanelType, MetadataTableName } from '@bechtel/sr-types';
import {
  CellClickedEvent,
  CellEditingStoppedEvent,
  CellKeyDownEvent,
  ColDef,
  ColGroupDef,
  GridOptions,
  IRowNode,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { cloneDeep, each, filter, find, findIndex, groupBy, isEmpty, isEqual, orderBy, sortBy } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { ErrorRetry, GridSkeleton, MultiLingualText, useLanguage } from '~/components';
import { GET_BREAKER_PANELS } from '~/gql';
import { DEFAULT_FONT_SIZE } from '../../grid/types';
import { PanelDiagram } from './panel-diagram';
import {
  BreakerModalStyled,
  ButtonStyled,
  GridAreaStyled,
  GridWrapperStyled,
  ModalFooterStyled,
  ModalHeaderStyled,
  PanelSectionStyled,
  rowHeight,
} from './styled';
import { BreakerModelProps, PanelItem, descriptionColumnWidth, panelInitialWidth } from './types';
import { useColumnHeading } from './use-column-heading';

const mapPanelItem = (panel: PanelItem, point?: BlockPoint): PanelItem => {
  return {
    ...panel,
    pointTag: point?.tag,
  };
};

enum PanelSide {
  L,
  R,
}

const flattenColumnDefs = (columnDefs) => {
  const flatDefs: ColDef[] = [];
  columnDefs.forEach((colDef: ColDef) => {
    // @ts-ignore colDef.children attribute is actually present but it is missing in the Ag-grid types
    if (colDef.children) {
      // @ts-ignore colDef.children attribute is actually present but it is missing in the Ag-grid types
      flatDefs.push(...flattenColumnDefs(colDef.children));
    } else {
      flatDefs.push(colDef);
    }
  });
  return flatDefs;
};

export const BreakerModal: FC<BreakerModelProps> = ({
  visible,
  close,
  onBreakersUpdated,
  panelType,
  block,
  canEdit,
  commodityId,
}) => {
  const theme = useTheme();
  const { getMultiLingualTextByKey } = useLanguage();

  const cableIdHeader = useColumnHeading(MetadataTableName.CABLE, 'tag');
  const equipmentIdHeader = useColumnHeading(MetadataTableName.EQUIPMENT, 'tag');
  const loadDescHeader = useColumnHeading(MetadataTableName.EQUIPMENT_BLOCK_POINT, 'description');
  const revisionHeader = useColumnHeading(MetadataTableName.EQUIPMENT_BLOCK_POINT, 'revision');
  const breakerSizeAmpsHeader = useColumnHeading(MetadataTableName.EQUIPMENT_BLOCK_POINT, 'breakerSizeAmps');
  const connectedLoadHeader = useColumnHeading(MetadataTableName.EQUIPMENT_BLOCK_POINT, 'connectedLoad');

  const [breakerPanels, setBreakerPanels] = useState<BreakerPanel[]>();
  const [initialBreakerPanels, setInitialBreakerPanels] = useState<BreakerPanel[]>();
  //The total number of points that can be ganged, it is 3 for AC_3P4W and 2 for AC_1P3W, other panel types cannot have ganging feature
  const maxGangable = panelType === BreakerPanelType.AC_3P4W ? 3 : panelType === BreakerPanelType.AC_1P3W ? 2 : 0;
  const leftGridRef = useRef<AgGridReact>(null);
  const rightGridRef = useRef<AgGridReact>(null);
  const { loading, error, refetch, networkStatus } = useQuery<
    { breakerPanels: BreakerPanel[] },
    { breakerPanelInput: BreakerPanelInput }
  >(GET_BREAKER_PANELS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    variables: {
      breakerPanelInput: {
        blockId: block.id,
        equipmentId: commodityId,
      },
    },
    onCompleted: (data) => {
      if (data.breakerPanels) {
        const orderedBreakerPanels = orderBy(
          data.breakerPanels,
          (breakerPanel: BreakerPanel) => breakerPanel.pointSequence
        );
        setBreakerPanels(orderedBreakerPanels);
        setInitialBreakerPanels(orderedBreakerPanels);
      }
    },
  });

  useEffect(() => {
    if (block && breakerPanels && block?.blockPoints) {
      // Create a set of existing pointIds from breakerPanels
      const breakerPanelsSet = new Set(breakerPanels.map((item) => item.pointId));

      // Filter out newly added blockPoints that are not in breakerPanelsSet
      const newlyAddedPoints = block.blockPoints.filter((blockPoint) => !breakerPanelsSet.has(blockPoint.id));

      if (newlyAddedPoints.length > 0) {
        // Create a new list of breakerPanels by adding the new points
        const updatedBreakerPanels = [
          ...breakerPanels,
          ...newlyAddedPoints.map((point) => ({
            blockId: block.id,
            pointSequence: point.sequenceNumber,
            gangSequence: point.sequenceNumber,
            pointId: point.id,
          })),
        ];

        // Update the breakerPanels state
        setBreakerPanels(updatedBreakerPanels);
      } else {
        // If there are no new points, check if some points were reverted
        const currentBlockPointsSet = new Set(block.blockPoints.map((point) => point.id));
        const filteredBreakerPanels = breakerPanels.filter((panel) => currentBlockPointsSet.has(panel.pointId ?? ''));

        if (!isEqual(filteredBreakerPanels, breakerPanels)) {
          setBreakerPanels(filteredBreakerPanels);
        }
      }
    }
  }, [block, breakerPanels, setBreakerPanels]);

  //Need to set the width programmatically as the grid needs to expand itself when the description column is expanded
  const [leftPanelWidth, setLeftPanelWidth] = useState(panelInitialWidth);
  const [rightPanelWidth, setRightPanelWidth] = useState(panelInitialWidth);
  //Data for the left and right grids
  const [leftPanelData, setLeftPanelData] = useState<PanelItem[]>([]);
  const [rightPanelData, setRightPanelData] = useState<PanelItem[]>([]);
  //same state is maintained for selected rows in both left and right panel. Only one side can have the rows selected at a time.
  const [selectedRows, setSelectedRows] = useState<PanelItem[]>([]);

  useEffect(() => {
    if (!breakerPanels?.length) return;

    //Group the panels based n gang sequence
    const groupedPanels = groupBy(breakerPanels, (breakerPanel) => breakerPanel.gangSequence);

    const displayPanels: PanelItem[] = [];

    //Logic to identify the number of rows a particular point should occupy(through rowSpan property)
    //Ganging is not possible for DC, so we are pseudo-ganging here.
    if (panelType === BreakerPanelType.DC) {
      const halfLength = Math.ceil(breakerPanels?.length / 2);
      const isHalfLengthOdd = halfLength % 2 !== 0;

      for (let i = 0; i < breakerPanels?.length; i++) {
        const panel = breakerPanels[i];

        // Adjust rowSpan based on the index and whether halfLength is odd
        if (isHalfLengthOdd && i >= breakerPanels.length - 2) {
          // For the last two panels in case halfLength is odd
          displayPanels.push({ ...panel, rowSpan: 1 });
        } else if (i % 4 < 2) {
          const panel = breakerPanels[i];
          displayPanels.push({ ...panel, rowSpan: 2 });
        }
      }
    } else {
      for (const gangSequence in groupedPanels) {
        displayPanels.push({ ...groupedPanels[gangSequence][0], rowSpan: groupedPanels[gangSequence].length });
      }
    }

    const leftPanelItems: PanelItem[] = [];
    const rightPanelItems: PanelItem[] = [];
    // Filter panels into left and right arrays with proper checks

    // Include logic for gangSequence = 0
    const leftPanels: PanelItem[] = displayPanels.filter(
      (panel: PanelItem) => panel.gangSequence === 0 || (panel.gangSequence && panel.gangSequence % 2 !== 0)
    );

    const rightPanels: PanelItem[] = displayPanels.filter(
      (panel: PanelItem) => panel.gangSequence && panel.gangSequence % 2 === 0
    );

    for (let i = 0; i < leftPanels?.length; i++) {
      const panelLeft = leftPanels[i];
      const pointLeft = find(block.blockPoints, (point) => point?.id === panelLeft?.pointId);
      if (panelLeft) leftPanelItems.push(mapPanelItem(panelLeft, pointLeft));
    }

    for (let i = 0; i < rightPanels?.length; i++) {
      const panelRight = rightPanels[i];
      const pointLeft = find(block.blockPoints, (point) => point?.id === panelRight?.pointId);
      if (panelRight) rightPanelItems.push(mapPanelItem(panelRight, pointLeft));
    }
    //Add a blank point to complete the grid
    if (panelType !== BreakerPanelType.DC && breakerPanels.length % 2 !== 0) {
      rightPanelItems.push({ rowSpan: 1, pointSequence: breakerPanels.length, gangSequence: breakerPanels.length });
    }
    setLeftPanelData(leftPanelItems);
    setRightPanelData(rightPanelItems);
  }, [block.blockPoints, breakerPanels, panelType]);

  const onGridCellClicked = useCallback(
    (event: CellClickedEvent, panelSide: PanelSide) => {
      const panelData = event.node.data;
      if (!maxGangable) {
        return;
      }

      if (panelSide === PanelSide.L) {
        rightGridRef.current?.api.deselectAll();
        rightGridRef.current?.api.clearRangeSelection();
      } else {
        leftGridRef.current?.api.deselectAll();
        leftGridRef.current?.api.clearRangeSelection();
      }

      const items = sortBy(selectedRows, (selectedItem) => selectedItem.pointSequence);
      let gangedItems = selectedRows;
      if (!gangedItems.length) {
        gangedItems = [panelData];
      } else if (
        panelData.pointSequence === (items[items?.length - 1]?.pointSequence || 0) + 2 &&
        items.length < maxGangable
      ) {
        gangedItems = [...selectedRows, panelData];
      } else if (panelData.pointSequence === (items[0].pointSequence || 0) - 2 && items.length < maxGangable) {
        gangedItems = [panelData, ...selectedRows];
      } else {
        gangedItems = [panelData];
      }
      setSelectedRows(gangedItems);

      const selectedNodes: IRowNode<PanelItem>[] = [];
      event.api.forEachNode((node) => {
        const index = findIndex(gangedItems, (item) => item.pointId === node.data.pointId);
        if (index > -1) {
          selectedNodes.push(node);
        }
      });
      event.api.deselectAll();
      event.api.clearRangeSelection();
      event.api.setNodesSelected({ nodes: selectedNodes, newValue: true });
    },
    [maxGangable, selectedRows]
  );

  const disabledCellStyle = useMemo(() => {
    return { backgroundColor: theme.colors.grey[100], textAlign: 'center' };
  }, [theme.colors.grey]);
  // Column Definitions: Defines & controls grid columns.
  const colDefLeft = useMemo<ColDef[]>(() => {
    return [
      {
        field: 'breakerRev',
        headerName: revisionHeader,
        width: 40,
        editable: canEdit,
        valueFormatter: (params) => params.value?.toUpperCase(),
      },
      {
        headerName: getMultiLingualTextByKey('Service'),
        children: [
          {
            field: 'cableTag',
            headerName: cableIdHeader,
            width: 70,
            editable: false,
            cellStyle: disabledCellStyle,
          },
          {
            field: 'toEquipmentTag',
            headerName: equipmentIdHeader,
            width: 110,
            editable: false,
            cellStyle: disabledCellStyle,
          },
          {
            field: 'description',
            headerName: loadDescHeader,
            flex: 1,
            resizable: true,
            minWidth: descriptionColumnWidth,
            editable: canEdit,
            valueFormatter: (params) => params.value?.toUpperCase(),
          },
        ],
      },
      {
        field: 'breakerSizeAmps',
        headerName: breakerSizeAmpsHeader,
        width: 60,
        editable: canEdit,
        type: 'numericColumn',
      },
      {
        field: 'connectedLoad',
        headerName: connectedLoadHeader,
        width: 50,
        editable: canEdit,
        type: 'numericColumn',
      },
      {
        field: 'pointTag',
        headerName: getMultiLingualTextByKey('Breaker'),
        width: 80,
        editable: false,
        cellStyle: disabledCellStyle,
      },
    ];
  }, [
    breakerSizeAmpsHeader,
    cableIdHeader,
    canEdit,
    connectedLoadHeader,
    disabledCellStyle,
    equipmentIdHeader,
    getMultiLingualTextByKey,
    loadDescHeader,
    revisionHeader,
  ]);

  const colDefRight = useMemo<(ColDef | ColGroupDef)[]>(() => {
    return [
      {
        field: 'pointTag',
        headerName: getMultiLingualTextByKey('Breaker'),
        width: 80,
        editable: false,
        cellStyle: disabledCellStyle,
      },
      {
        field: 'connectedLoad',
        headerName: connectedLoadHeader,
        width: 50,
        editable: canEdit,
        type: 'numericColumn',
      },
      {
        field: 'breakerSizeAmps',
        headerName: breakerSizeAmpsHeader,
        width: 60,
        editable: canEdit,
        type: 'numericColumn',
      },
      {
        headerName: getMultiLingualTextByKey('Service'),
        children: [
          {
            field: 'cableTag',
            headerName: cableIdHeader,
            width: 70,
            editable: false,
            cellStyle: disabledCellStyle,
          },
          {
            field: 'toEquipmentTag',
            headerName: equipmentIdHeader,
            width: 110,
            editable: false,
            cellStyle: disabledCellStyle,
          },
          {
            field: 'description',
            headerName: loadDescHeader,
            flex: 1,
            resizable: true,
            minWidth: descriptionColumnWidth,
            editable: canEdit,
            valueFormatter: (params) => params.value?.toUpperCase(),
          },
        ],
      },
      {
        field: 'breakerRev',
        headerName: revisionHeader,
        width: 40,
        editable: canEdit,
        valueFormatter: (params) => params.value?.toUpperCase(),
      },
    ];
  }, [
    breakerSizeAmpsHeader,
    cableIdHeader,
    canEdit,
    connectedLoadHeader,
    disabledCellStyle,
    equipmentIdHeader,
    getMultiLingualTextByKey,
    loadDescHeader,
    revisionHeader,
  ]);

  const defaultColumnDefs = useMemo<ColDef>(() => {
    return {
      editable: false,
      sortable: false,
      resizable: false,
      suppressColumnsToolPanel: true,
      suppressHeaderMenuButton: true,
      suppressMovable: true,
      ...(!canEdit && { cellStyle: disabledCellStyle }),
    };
  }, [disabledCellStyle, canEdit]);

  const gridOptionsLeft: GridOptions = useMemo(() => {
    return {
      getRowHeight: (params) => {
        if (params.node.data.rowSpan) {
          return params.node.data.rowSpan * rowHeight;
        }
        return rowHeight;
      },
      onColumnResized: (event) => {
        // Description column is resizable and has flex set to 1. We don't want to resize the grid when the event source is flex(This happens when the grid is rendered first time)
        //If we don't return here, description will keep expanding continuously when the grid is rendered.
        if (event.source === 'flex') {
          return;
        }

        const gridWidth = (event.column?.getActualWidth() || 0) - descriptionColumnWidth;
        setLeftPanelWidth(gridWidth + panelInitialWidth);
      },
      suppressNavigateAfterEdit: true,
    };
  }, []);

  const gridOptionsRight: GridOptions = useMemo(() => {
    return {
      getRowHeight: (params) => {
        if (params.node.data.rowSpan) {
          return params.node.data.rowSpan * rowHeight;
        }
        return rowHeight;
      },
      onColumnResized: (event) => {
        // Description column is resizable and has flex set to 1. We don't want to resize the grid when the event source is flex(This happens when the grid is rendered first time)
        //If we don't return here, description will keep expanding continuously when the grid is rendered.
        if (event.source === 'flex') {
          return;
        }

        const gridWidth = (event.column?.getActualWidth() || 0) - descriptionColumnWidth;
        setRightPanelWidth(gridWidth + panelInitialWidth);
      },
    };
  }, []);

  const canUnGang = () => {
    if (selectedRows && selectedRows.length > 0)
      return (
        filter(breakerPanels, (breakerPanel) => breakerPanel.gangSequence === selectedRows[0]?.gangSequence)?.length > 1
      );
    return false;
  };

  const onGanging = () => {
    if (!selectedRows || !breakerPanels) return;

    const toBeUpdatedBreakerPanels = cloneDeep(breakerPanels);
    //Un-gang
    if (selectedRows.length === 1) {
      const toBeUnGanged = filter(
        breakerPanels,
        (breakerPanel) => breakerPanel.gangSequence === selectedRows[0].gangSequence
      );
      each(toBeUnGanged, (panelItem) => {
        const index = findIndex(toBeUpdatedBreakerPanels, (panel: BreakerPanel) => panel.pointId === panelItem.pointId);
        if (index > -1) {
          const existingItem = toBeUpdatedBreakerPanels[index];
          toBeUpdatedBreakerPanels.splice(index, 1, {
            ...existingItem,
            gangSequence: existingItem.pointSequence,
            description: toBeUnGanged[0].description,
            breakerSizeAmps: toBeUnGanged[0].breakerSizeAmps,
            connectedLoad: toBeUnGanged[0].connectedLoad,
            breakerRev: toBeUnGanged[0].breakerRev,
          });
        }
      });
    } else {
      //Gang
      each(selectedRows, (panelItem) => {
        const index = findIndex(toBeUpdatedBreakerPanels, (panel: BreakerPanel) => panel.pointId === panelItem.pointId);
        if (index > -1) {
          const existingItem = toBeUpdatedBreakerPanels[index];
          const gangedRow = {
            ...existingItem,
            gangSequence: selectedRows[0].pointSequence,
            description: selectedRows[0].description,
            breakerSizeAmps: selectedRows[0].breakerSizeAmps,
            connectedLoad: selectedRows[0].connectedLoad,
            breakerRev: selectedRows[0].breakerRev,
          };
          toBeUpdatedBreakerPanels.splice(index, 1, gangedRow);
          setSelectedRows([{ ...gangedRow, rowSpan: selectedRows.length }]);
        }
      });
    }
    setBreakerPanels([...toBeUpdatedBreakerPanels]);
    //Set the focus to the correct row
    const leftRowIndex = leftPanelData.findIndex((item) => item.pointTag === selectedRows[0].pointTag);
    const rightRowIndex = rightPanelData.findIndex((item) => item.pointTag === selectedRows[0].pointTag);
    const rowIndex = leftRowIndex !== -1 ? leftRowIndex : rightRowIndex;
    const colDef = leftRowIndex !== -1 ? colDefLeft : colDefRight;
    const gridRef = leftRowIndex !== -1 ? leftGridRef : rightGridRef;
    // @ts-ignore colDef.field attribute is actually present but it is missing in the Ag-grid types
    gridRef?.current?.api?.setFocusedCell(rowIndex, colDef[0].field);
  };
  const totalRows = Math.round((block.blockPoints?.length || 0) / 2);
  const onCellEditingStopped = useCallback(async (params: CellEditingStoppedEvent) => {
    const { data } = params;
    if (!data) return;
    setBreakerPanels((prevBreakerPanels) => {
      const existingBreakerPanels = cloneDeep(prevBreakerPanels);
      const obj = existingBreakerPanels?.find((item) => item.pointId === data.pointId);
      if (obj) {
        Object.assign(obj, data);
      }
      return existingBreakerPanels;
    });
  }, []);

  const handleCellKeyDown = useCallback(
    (event: CellKeyDownEvent, panelSide: PanelSide) => {
      const columnDefs = panelSide === PanelSide.L ? colDefLeft : colDefRight;
      const rowData = panelSide === PanelSide.L ? leftPanelData : rightPanelData;
      const flatColumnDefs = flattenColumnDefs(columnDefs);
      const currentColumnId = event.column?.getColDef()?.field;
      const lastEditableColumn = flatColumnDefs?.findLast((item) => item.editable)?.field;
      const isLastColumn = currentColumnId === lastEditableColumn;
      const lastRow = rowData ? rowData[event.rowIndex ?? 0] : null;
      const firstEditableColumn = flatColumnDefs?.find((item) => item.editable)?.field ?? '';
      const currentColumnIndex = flatColumnDefs?.findIndex((col) => col.field === currentColumnId);
      const nextEditableColumn = flatColumnDefs?.slice(currentColumnIndex + 1).find((col) => col.editable)?.field ?? '';
      // @ts-ignore event.key attribute is actually present but it is missing in the Ag-grid types
      if (event.event?.key === 'Tab' && currentColumnId !== lastEditableColumn) {
        setTimeout(() => {
          if (event.rowIndex !== null) {
            event.api.startEditingCell({
              rowIndex: event.rowIndex,
              colKey: nextEditableColumn,
            });
          }
        }, 200);
      }

      if (
        // @ts-ignore event.key attribute is actually present but it is missing in the Ag-grid types
        event.event?.key === 'Tab' &&
        isLastColumn &&
        (!isEmpty(lastRow) || firstEditableColumn === lastEditableColumn)
      ) {
        setTimeout(() => {
          if (event.rowIndex !== null) {
            event.api.startEditingCell({
              rowIndex: event.rowIndex + 1,
              colKey: firstEditableColumn,
            });
          }
        }, 200);
      }
    },
    [colDefLeft, colDefRight, leftPanelData, rightPanelData]
  );
  const modalTitle = getMultiLingualTextByKey('BREAKER_PANEL_LAYOUT');
  return (
    <BreakerModalStyled visible={visible}>
      <ModalHeaderStyled title={modalTitle}>
        <Box css={{ marginLeft: 'auto ' }}>
          <Box alignItems={{ sm: 'center' }} p={{ sm: '400' }}>
            <Button size={ButtonSize.SMALL} css={{ background: 'transparent', boxShadow: 'none' }} onClick={close}>
              <Icon icon="times" iconStyle="regular" size={IconSize.LARGE} />
            </Button>
          </Box>
        </Box>
      </ModalHeaderStyled>
      {(loading || networkStatus === NetworkStatus.refetch) && <GridSkeleton />}

      {error && <ErrorRetry refetch={refetch} />}
      {!loading && !error && (
        <>
          {/* @ts-ignore adjust in GDS component to take child as prop  */}
          <ModalBody
            css={`
              flex-direction: column;
            `}
          >
            <Box px={{ sm: '500' }} pt={{ sm: '300' }} alignSelf={{ sm: 'flex-end' }}>
              <ButtonStyled
                onClick={onGanging}
                disabled={
                  !canEdit ||
                  panelType === BreakerPanelType.AC_1P2W ||
                  panelType === BreakerPanelType.DC ||
                  (!canUnGang() && selectedRows.length < 2)
                }
              >
                {canUnGang() ? (
                  <Box gap={{ sm: '300' }}>
                    <Icon icon="split" iconStyle="regular" />
                    <MultiLingualText weight={'600'}>Ungang</MultiLingualText>
                  </Box>
                ) : (
                  <Box gap={{ sm: '300' }}>
                    <Icon icon="merge" iconStyle="regular" />
                    <MultiLingualText weight={'600'}>Gang</MultiLingualText>
                  </Box>
                )}
              </ButtonStyled>
            </Box>
            <GridAreaStyled>
              <PanelSectionStyled>
                <GridWrapperStyled fontSize={DEFAULT_FONT_SIZE} style={{ width: leftPanelWidth }}>
                  <AgGridReact
                    rowData={leftPanelData}
                    columnDefs={colDefLeft}
                    ref={leftGridRef}
                    domLayout="autoHeight"
                    suppressHorizontalScroll={true}
                    defaultColDef={defaultColumnDefs}
                    rowHeight={26}
                    headerHeight={26}
                    suppressContextMenu={true}
                    suppressRowTransform={true}
                    suppressAutoSize={true}
                    gridOptions={gridOptionsLeft}
                    suppressDragLeaveHidesColumns={true}
                    rowSelection="multiple"
                    enableRangeSelection={true}
                    suppressRowClickSelection
                    onCellClicked={(event) => onGridCellClicked(event, PanelSide.L)}
                    onCellEditingStopped={(params) => onCellEditingStopped(params)}
                    onCellKeyDown={(event: CellKeyDownEvent) => handleCellKeyDown(event, PanelSide.L)}
                  />
                </GridWrapperStyled>
                <div>
                  <PanelDiagram panelType={panelType} rowCount={totalRows}></PanelDiagram>
                </div>
                <GridWrapperStyled fontSize={DEFAULT_FONT_SIZE} style={{ width: rightPanelWidth }}>
                  <AgGridReact
                    rowData={rightPanelData}
                    columnDefs={colDefRight}
                    ref={rightGridRef}
                    domLayout="autoHeight"
                    suppressHorizontalScroll={true}
                    defaultColDef={defaultColumnDefs}
                    rowHeight={26}
                    headerHeight={26}
                    suppressContextMenu={true}
                    suppressRowTransform={true}
                    suppressAutoSize={true}
                    gridOptions={gridOptionsRight}
                    suppressDragLeaveHidesColumns={true}
                    rowSelection="multiple"
                    enableRangeSelection={true}
                    suppressRowClickSelection
                    onCellEditingStopped={(params) => onCellEditingStopped(params)}
                    onCellClicked={(event) => onGridCellClicked(event, PanelSide.R)}
                    onCellKeyDown={(event: CellKeyDownEvent) => handleCellKeyDown(event, PanelSide.R)}
                  />
                </GridWrapperStyled>
              </PanelSectionStyled>
            </GridAreaStyled>
          </ModalBody>
          <ModalFooterStyled>
            <Box p={{ sm: '500' }}>
              <ButtonStyled
                onClick={() => {
                  setBreakerPanels(initialBreakerPanels);
                  close();
                }}
                secondary={true}
              >
                <Box px={{ sm: '500' }}>
                  <MultiLingualText>Cancel</MultiLingualText>
                </Box>
              </ButtonStyled>
              <ButtonStyled
                onClick={() => {
                  onBreakersUpdated(breakerPanels ?? []);
                }}
                disabled={isEqual(initialBreakerPanels, breakerPanels)}
              >
                <Box px={{ sm: '500' }}>
                  <MultiLingualText>OK</MultiLingualText>
                </Box>
              </ButtonStyled>
            </Box>
          </ModalFooterStyled>
        </>
      )}
    </BreakerModalStyled>
  );
};
